import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import api from "../../../utils/axios";
import moment from "moment";
import "moment/locale/th";
import Swal from "sweetalert2";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Pagination from "../../components/Pagination";
import DatePicker from "../../components/DatePicker";
import { Icon } from "@iconify/react";
import AppointmentDetailModal from "../../components/Modal/AppointmentDetailModal";

export default function SaleAppointDetail() {
  const { Emp_Id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get("date");

  const [data, setData] = useState([]);
  const [empName, setEmpName] = useState("");
  const [selectedDate, setSelectedDate] = useState(dateParam || "");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // ⭐ DEFAULT 50

  // SORT STATE
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAppoint, setSelectedAppoint] = useState(null);

  const loadData = async () => {
    try {
      const url = selectedDate
        ? `/get-sale-appoint-detail?Emp_Id=${Emp_Id}&date=${selectedDate}`
        : `/get-sale-appoint-detail?Emp_Id=${Emp_Id}`;

      const res = await api.get(url);

      if (res.data?.status === "OK") {
        setData(res.data.result || []);
        setEmpName(res.data.empName || "");
      }
    } catch (err) {
      console.error("โหลดข้อมูลผิดพลาด", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const fetchData = async (date = "") => {
    setLoading(true);
    try {
      const url = date
        ? `/get-sale-appoint-detail?Emp_Id=${Emp_Id}&date=${date}`
        : `/get-sale-appoint-detail?Emp_Id=${Emp_Id}`;
      const res = await api.get(url);

      if (res.data?.status === "OK") {
        setData(res.data.result || []);
        setEmpName(res.data.empName || "");
      } else {
        Swal.fire("ผิดพลาด", res.data?.message || "ไม่สามารถโหลดข้อมูลได้", "error");
      }
    } catch (err) {
      Swal.fire("ผิดพลาด", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialDate = dateParam || new Date().toISOString().split("T")[0];
    setSelectedDate(initialDate);
    fetchData(initialDate);
  }, [Emp_Id]);

  // ESC = back
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        localStorage.setItem("saleReportDate", selectedDate);
        localStorage.setItem("saleReportSearch", searchText);
        localStorage.setItem("saleReportTab", "appoint");

        window.location.href = `/sale-report?date=${selectedDate}`;
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedDate, searchText]);

  // FORMAT ----------------------------------------------------------------------

  const formatPhone = (tel) => {
    if (!tel) return "-";
    const digits = tel.replace(/\D/g, "").slice(0, 10);
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const formatThaiDateTimeFull = (dateStr, timeStr) => {
    if (!dateStr) return "-";
    try {
      const d = moment(dateStr);
      const buddhistYear = d.year() + 543;
      const formattedDate = d.format(`DD/MMM/${buddhistYear}`);

      if (!timeStr) return formattedDate;

      let hh = "";
      let mm = "";

      if (timeStr.includes("T")) {
        hh = timeStr.substring(11, 13);
        mm = timeStr.substring(14, 16);
      } else {
        const parts = timeStr.split(":");
        hh = parts[0] || "00";
        mm = parts[1] || "00";
      }

      return `${formattedDate} ${hh}:${mm}`;
    } catch {
      return "-";
    }
  };

  // FILTER (ค้นหาทุกช่อง)
  const filteredData = data.filter((d) => {
    const t = searchText.toLowerCase();

    return (
      (d.AR_Name || "").toLowerCase().includes(t) ||
      (d.AR_LastName || "").toLowerCase().includes(t) ||
      (d.DisplayName || "").toLowerCase().includes(t) ||
      (d.AR_Tel || "").toLowerCase().includes(t) ||
      (d.Contact_Name || "").toLowerCase().includes(t) ||
      (d.Contact_Position || "").toLowerCase().includes(t) ||
      (d.SpecRemark || "").toLowerCase().includes(t)
    );
  });

  const getDateTimeValue = (appointDate, startTime) => {
    if (!appointDate) return 0;

    let date = moment(appointDate);

    let hh = "00";
    let mm = "00";

    if (startTime) {
      // ISO เช่น 2025-01-10T09:20:00.000Z
      if (startTime.includes("T")) {
        hh = startTime.substring(11, 13);
        mm = startTime.substring(14, 16);
      } else {
        // เวลา 09:20:00
        const p = startTime.split(":");
        hh = p[0] || "00";
        mm = p[1] || "00";
      }
    }

    date = date.hour(Number(hh)).minute(Number(mm));

    return date.valueOf(); // timestamp ใช้ sort ได้ดีสุด
  };


  // SORT
  const sortedData = React.useMemo(() => {
    let sorted = [...filteredData];

    if (!sortConfig.key) return sorted;

    sorted.sort((a, b) => {
      const key = sortConfig.key;

      // ⭐ กรณีคอลัมน์วันเวลาที่เข้าพบ → sort datetime จริง
      if (key === "Appoint_Date") {
        const x = getDateTimeValue(a.Appoint_Date, a.Time_Start);
        const y = getDateTimeValue(b.Appoint_Date, b.Time_Start);
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      // ⭐ อื่นๆ เป็น string ปกติ
      const x = (a[key] || "").toString().toLowerCase();
      const y = (b[key] || "").toString().toLowerCase();

      if (sortConfig.direction === "asc") return x > y ? 1 : -1;
      return x < y ? 1 : -1;
    });

    return sorted;
  }, [filteredData, sortConfig]);


  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // PAGINATE
  const totalItems = sortedData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  // UI ----------------------------------------------------------------------------

  const breadcrumbs = [
    { label: "ขาย", link: "/sale" },
    { label: "ตรวจสอบฝ่ายขาย", link: "/sale-report" },
    { label: "รายละเอียดการนัดหมาย" },
  ];

  const SortIcon = ({ column }) => (
    <span style={{ fontSize: "11px", marginLeft: "6px" }}>
      <span style={{ color: sortConfig.key === column && sortConfig.direction === "asc" ? "#fff" : "#bbb" }}>▲</span>
      <span style={{ color: sortConfig.key === column && sortConfig.direction === "desc" ? "#fff" : "#bbb", marginLeft: "2px" }}>▼</span>
    </span>
  );

  const formatTimeRange = (s, e) => {
    if (!s && !e) return "-";

    let ss = "";
    let ee = "";

    // เวลาแบบ ISO
    if (s?.includes("T")) ss = s.substring(11, 16);
    else if (s) ss = s.substring(0, 5);

    if (e?.includes("T")) ee = e.substring(11, 16);
    else if (e) ee = e.substring(0, 5);

    return ss && ee ? `${ss}-${ee}` : ss || ee || "-";
  };


  const formatThaiDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = moment(dateStr);
      const buddhistYear = d.year() + 543;
      return d.format(`DD/MMM/${buddhistYear}`);
    } catch {
      return "-";
    }
  };


  return (
    <>
      <Sidebar />
      <Topbar breadcrumbs={breadcrumbs} />

      <div
        className="p-6"
        style={{
          marginLeft: "var(--sidebar-width)",
          marginTop: "56px",
          backgroundColor: "#f8f9fa",
          minHeight: "calc(100vh - 56px)",
        }}
      >
        <div className="card border-0 shadow-sm p-3">

          {/* FILTER + HEADER */}
          <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
            <h3 className="fw-bold text-gray-700 fs-5 m-0">
              รายละเอียดนัดหมายเข้าพบ {empName || "-"}
            </h3>

            <input
              type="text"
              placeholder="ค้นหา..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="form-control"
              style={{ width: "260px", height: "38px" }}
            />

            <button
              className="btn btn-outline-secondary"
              style={{ width: "36px", height: "36px", padding: 0 }}
              onClick={() => {
                const prev = moment(selectedDate).subtract(1, "day").format("YYYY-MM-DD");
                setSelectedDate(prev);
                fetchData(prev);
              }}
            >
              ◀
            </button>

            <div style={{ width: "230px" }}>
              <DatePicker
                name="selectedDate"
                value={selectedDate}
                onChange={(d) => {
                  setSelectedDate(d);
                  fetchData(d);
                }}
                className="form-control text-center"
                style={{ height: "36px" }}
              />
            </div>

            <button
              className="btn btn-outline-secondary"
              style={{ width: "36px", height: "36px", padding: 0 }}
              onClick={() => {
                const next = moment(selectedDate).add(1, "day").format("YYYY-MM-DD");
                setSelectedDate(next);
                fetchData(next);
              }}
            >
              ▶
            </button>

            <button
              className="btn btn-outline-danger"
              style={{ height: "38px", padding: "0 16px" }}
              onClick={() => {
                const today = new Date().toISOString().split("T")[0];
                setSelectedDate(today);
                fetchData(today);
              }}
            >
              รีเซ็ตวันนี้
            </button>
          </div>

          {/* TABLE */}
          <style>{`
            table.table {
              border-collapse: collapse;
              width: 100%;
            }
            th {
              background-color: #E13F57 !important;
              color: white !important;
              cursor: pointer;
              user-select: none;
            }
            th, td {
              border: 1px solid #ccc !important;
              white-space: nowrap;
              padding: 6px 10px !important;
              vertical-align: middle !important;
            }
            tbody tr:nth-child(odd) { background: #fafafa; }
            tbody tr:nth-child(even) { background: #fff; }
            tbody tr:hover { background: #f5f7ff !important; }
          `}</style>

          <div className="table-responsive" style={{ maxHeight: "70vh" }}>
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th className="text-center" onClick={() => requestSort("Appoint_Id")}>
                    ลำดับ <SortIcon column="Appoint_Id" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("AR_Name")}>
                    ชื่อลูกค้า <SortIcon column="AR_Name" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("AR_Tel")}>
                    เบอร์ <SortIcon column="AR_Tel" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("Contact_Name")}>
                    ผู้รับผิดชอบ <SortIcon column="Contact_Name" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("Contact_Position")}>
                    ตำแหน่ง <SortIcon column="Contact_Position" />
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("Appoint_Date")}
                    style={{ cursor: "pointer" }}
                  >
                    วันเวลาที่เริ่มเข้าพบ{" "}
                    {sortConfig.key === "Appoint_Date"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>



                  <th className="text-center">สถานะ</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      กำลังโหลด…
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <tr key={row.Appoint_Id || idx}>
                      <td className="text-center">{startIndex + idx + 1}</td>

                      <td className="text-start">
                        {row.DisplayName ||
                          `${row.AR_Name || ""} ${row.AR_LastName || ""}`.trim()}
                      </td>

                      <td>{formatPhone(row.AR_Tel)}</td>
                      <td>{row.Contact_Name || "-"}</td>
                      <td>{row.Contact_Position || "-"}</td>

                      <td className="text-center">
                        {formatThaiDate(row.Appoint_Date)}{" "}
                        {formatTimeRange(row.Time_Start, row.Time_End)}
                      </td>


                      <td className="position-relative text-center">
                        {row.SpecRemark ? (
                          row.DistanceStatus === "อยู่นอกระยะทำการ" ? (
                            <span className="bg-danger text-white px-3 py-1 rounded-pill">
                              นอกระยะทำการ
                            </span>
                          ) : (
                            <span className="bg-success text-white px-3 py-1 rounded-pill">
                              เสร็จสิ้น
                            </span>
                          )
                        ) : (
                          <span className="bg-dark text-white px-3 py-1 rounded-pill">
                            ยังไม่มีการเข้าพื้นที่
                          </span>
                        )}

                        <button
                          className="btn btn-light btn-sm position-absolute"
                          style={{ right: "6px", top: "50%", transform: "translateY(-50%)" }}
                          onClick={() => {
                            setSelectedAppoint(row);
                            setShowDetailModal(true);
                          }}
                        >
                          <Icon icon="mdi:eye" width="18" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      ไม่พบข้อมูลนัดหมาย
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="p-2 border-top bg-white">
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </div>

        {/* Back Button */}
        <div className="d-flex justify-content-end mt-3">
          <button
            onClick={() => {
              localStorage.setItem("saleReportDate", selectedDate);
              localStorage.setItem("saleReportSearch", searchText);
              localStorage.setItem("saleReportTab", "appoint");
              window.location.href = `/sale-report?date=${selectedDate}`;
            }}
            className="btn btn-dark btn-sm px-4 py-2"
            style={{ fontSize: "14px" }}
          >
            กลับหน้าค้นหา
          </button>
        </div>
      </div>

      {showDetailModal && (
        <AppointmentDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          appoint={selectedAppoint}
          onUpdated={loadData}
        />
      )}
    </>
  );
}
